import classNames from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as AddIcon } from '../../assets/images/icons/add_16.svg';
import { ReactComponent as SubtractIcon } from '../../assets/images/icons/subtract_16.svg';
import useHardwareFirstMembershipTerms from '../../hooks/useHardwareFirstMembershipTerms';
import useHardwareFirstTrialTerms from '../../hooks/useHardwareFirstTrialTerms';
import { lowestMonthlyPrice } from '../../lib/hardwareFirst';
import HardwareFirstTrialModal from '../HardwareFirstTrialToggle/HardwareFirstTrialModal';
import { Image } from '../Image';
import * as types from '../../types';
import styles from './HardwareFirstCartItem.module.scss';

export type HardwareFirstDevice = 'mini' | 'series3' | 'ultra';

interface IncludedItem {
  nameKey: string;
  descriptionKey: string;
}

/**
 * What ships in the box per device, listed under the card's "What's included?" toggle. The
 * hardware-first cart sells hardware alone, so these are contents rather than the priced bundle
 * rows the membership cart shows.
 */
const INCLUDED_ITEMS: Record<HardwareFirstDevice, IncludedItem[]> = {
  mini: [
    { nameKey: 'hardware_first_cart_mini_tracker', descriptionKey: 'hardware_first_cart_mini_battery' },
    { nameKey: 'hardware_first_cart_mini_cases', descriptionKey: 'hardware_first_cart_mini_cases_sub' },
    { nameKey: 'hardware_first_cart_charging_cable', descriptionKey: 'hardware_first_cart_charging_cable_sub' },
  ],
  series3: [
    { nameKey: 'hardware_first_cart_s3_tracker', descriptionKey: 'hardware_first_cart_s3_battery' },
    { nameKey: 'hardware_first_cart_s3_band', descriptionKey: 'hardware_first_cart_s3_band_sub' },
    { nameKey: 'hardware_first_cart_s3_base', descriptionKey: 'hardware_first_cart_s3_base_sub' },
  ],
  ultra: [
    { nameKey: 'hardware_first_cart_ultra_tracker', descriptionKey: 'hardware_first_cart_ultra_battery' },
    { nameKey: 'hardware_first_cart_charging_cable', descriptionKey: 'hardware_first_cart_charging_cable_sub' },
  ],
};

interface HardwareFirstCartItemProps {
  /** Absent for a non-tracker line such as a band, which drops the plan line and the box contents. */
  device?: HardwareFirstDevice;
  productImageUrl: string;
  productName: string;
  /** Variant line under the name; falls back to the device's own descriptor when there is none. */
  variantDescription?: string;
  /** Absent only while a just-added row waits on the repriced total; the card renders regardless. */
  price?: string;
  /** Struck-through list price. Absent when the server did not reprice the item. */
  listPrice?: string;
  /** Formatted multi-device saving on this tracker; the pill names it while the server priced one in. */
  multiDeviceDiscount?: string;
  /** Whole percent the promo code took off this tracker; 0 or absent without a code. */
  promoPercentOff?: number;
  removeCartItem?: () => void;
  /** The tracker is on the 30-day trial and costs nothing today. */
  isTrial?: boolean;
  /** The trial tracker's list price; absent until the server has priced it. */
  trialListPriceInCents?: number;
}

/**
 * Hardware-first cart row: a card carrying the repriced hardware, the plan-at-activation line, and
 * an expandable box-contents list. Scoped to the arm — the control cart keeps `ProductCartItem`.
 */
export default function HardwareFirstCartItem({
  device,
  productImageUrl,
  productName,
  variantDescription,
  price,
  listPrice,
  multiDeviceDiscount,
  promoPercentOff = 0,
  removeCartItem,
  isTrial = false,
  trialListPriceInCents,
}: HardwareFirstCartItemProps) {
  const { t } = useTranslation();
  const lowestMonthly = lowestMonthlyPrice(useHardwareFirstMembershipTerms());
  const trialTerms = useHardwareFirstTrialTerms(trialListPriceInCents);
  const [expanded, setExpanded] = useState(false);
  const includedItems = device ? INCLUDED_ITEMS[device] : [];
  // one pill for both savings: the code's percent and, once the cart holds two, the multi-tracker amount
  const discountPill =
    multiDeviceDiscount && promoPercentOff > 0
      ? t('hardware_first_cart_percent_plus_amount_off', { percent: promoPercentOff, amount: multiDeviceDiscount })
      : multiDeviceDiscount
        ? t('hardware_first_amount_off', { amount: multiDeviceDiscount })
        : promoPercentOff > 0
          ? t('hardware_first_percent_off', { percent: promoPercentOff })
          : undefined;

  return (
    <div className={styles.card}>
      <div className={classNames(styles.body, { [styles.bodyOnly]: includedItems.length === 0 })}>
        <div className={styles.top}>
          <div className={styles.thumb}>
            <Image image={{ type: 'image', url: productImageUrl }} alt={productName} />
          </div>
          <div className={styles.meta}>
            <h3 className={styles.name}>{productName}</h3>
            {(variantDescription || device) && (
              <p className={styles.variant}>{variantDescription ?? t('hardware_first_cart_fits_any_collar')}</p>
            )}
            {!isTrial && discountPill && <span className={styles.pill}>{discountPill}</span>}
            {isTrial && (
              <p className={styles.trialLine}>
                {t('hardware_first_cart_free_trial')}
                {trialTerms && (
                  <>
                    {' · '}
                    <HardwareFirstTrialModal
                      productCategory={
                        device === 'mini' ? types.ProductCategory.MINI : types.ProductCategory.COLLAR_KIT
                      }
                      terms={trialTerms}
                      trigger={
                        <button className={styles.trialLearnMore} type="button">
                          {t('learn_more')}
                        </button>
                      }
                    />
                  </>
                )}
              </p>
            )}
          </div>
          <div className={styles.price}>
            {price && <span className={styles.priceMain}>{price}</span>}
            {!isTrial && listPrice && <span className={styles.listPrice}>{listPrice}</span>}
            {removeCartItem && (
              <button className={styles.remove} onClick={removeCartItem} type="button">
                {t('remove')}
              </button>
            )}
          </div>
          {/* its own row under the name and the price, so it runs the width of both and stays one line */}
          {isTrial ? (
            <p className={styles.planLine}>{t('hardware_first_cart_trial_plan_required')}</p>
          ) : (
            device &&
            lowestMonthly && (
              <p className={styles.planLine}>
                {t('hardware_first_cart_requires_gps_plan', { monthly: lowestMonthly })}
              </p>
            )
          )}
        </div>
        {includedItems.length > 0 && (
          <div className={styles.included}>
            <button
              aria-expanded={expanded}
              className={styles.includedToggle}
              onClick={() => setExpanded((wasExpanded) => !wasExpanded)}
              type="button"
            >
              <span className={styles.includedLabel}>
                {t('cart_item_v2.whats_included')}{' '}
                {t('cart_item_v2.whats_included_count', { count: includedItems.length })}
              </span>
              {expanded ? (
                <SubtractIcon className={styles.includedIcon} />
              ) : (
                <AddIcon className={styles.includedIcon} />
              )}
            </button>
            {/* always rendered: the panel animates between a zero and a content-sized grid row */}
            <div
              aria-hidden={!expanded}
              className={classNames(styles.includedPanel, { [styles.includedPanelOpen]: expanded })}
            >
              <div className={styles.includedList}>
                {includedItems.map((item) => (
                  <div className={styles.includedRow} key={item.nameKey}>
                    <div className={styles.includedText}>
                      <span className={styles.includedName}>{t(item.nameKey)}</span>
                      <span className={styles.includedDescription}>{t(item.descriptionKey)}</span>
                    </div>
                    <span className={styles.includedValue}>{t('hardware_first_cart_included')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
